import { notify } from "@/common/notifications";
import Checkbox from "@imminently/decisively-ui/Checkbox";
import DataTable, { type DataTableColumn } from "@imminently/decisively-ui/DataTable";
import Dialog from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import Select, { type SelectOptions } from "@imminently/decisively-ui/Select";
import { Skeleton } from "@mui/material";
// import { Chip } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import get from "lodash/get";
import React from "react";
import { useTranslation } from "react-i18next";

const AutocompleteLoading = ({ className }: { className?: string }) => <Skeleton className={className} />;

/**
 * This section will describe some fundamental assumptions and
 * guidelines used when developing this component.
 *
 * Basically there are two "sources" (kinda) of information:
 * - we first extract all attributes from the graph that are related to
 * an entity that is currently considered to be a contact
 * - then we query special endpoint that returns a mapping stating which
 * attributes are mapped and which are not
 * It seems taht mapping from BE might return containing only subset of
 * all attributes in a graph and potentially might not have attributes that
 * we extracted directly from the graph using entity name. But to build
 * exhaustiv set of options we need to have all possible attributes and
 * then select only those that have no `mappedTo` property set or (I guess)
 * are not yet selected in current modal.
 *
 * Based on this there seems to be two data structures reqired for this
 * component to function properly:
 * - set of all possible values (from BE combined with extracted from
 * graph) with their corresponding mappings
 * - local editable mapping state that is based on attributes extracted from
 * current rule graph
 *
 * With these two structures we should be able to get all the available
 * options: first select those BE attributes that have no "mappedTo", then
 * additionally remove those options that are currently mapped to something
 * using local editable state.
 *
 * NOTE: two described structures are really coupled with one another, so
 * it seems best to have them inside the same state. That way we would be
 * able to derive mapFromBE using attributes from graph and set mapping
 * of attributes from graph using mapFromBE
 */
/** */

export type AttrMetadataMap = Record<string, { mappedTo?: string; disabled?: boolean } | undefined>;
export type ContactsMappingModalProps = {
  /**
   * basically array of descriptions of all the nodes that belong to\
   * current contact entity
   */
  fieldsOnContactObject: string[];
  getMapping: () => Promise<{
    entity: string;
    fields: AttrMetadataMap;
  }>;
  save: (v: AttrMetadataMap) => unknown;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type LocalMapping = {
  loading: boolean;
  mapFromBE: AttrMetadataMap;
  editableMap: AttrMetadataMap;
};
const getLocalMapping = (): LocalMapping => ({
  loading: false,
  mapFromBE: {},
  editableMap: {},
});

export const ContactsMappingModal: React.FC<ContactsMappingModalProps> = (p) => {
  const { fieldsOnContactObject, getMapping, save, open, onOpenChange } = p;
  const { t } = useTranslation();
  // const mappedAsIsStr = t('contacts_mapping_modal.mapped_as_is');
  const specialOptions = [
    {
      label: t("contacts_mapping_modal.mapped_as_is"),
      value: undefined, // TODO - consider using "mappedAsIs" as the value (the backend does support this now)
    },
    {
      label: t("contacts_mapping_modal.first_name"),
      value: "firstName",
    },
    {
      label: t("contacts_mapping_modal.last_name"),
      value: "lastName",
    },
    {
      label: t("contacts_mapping_modal.name"),
      value: "name",
    },
  ];
  const closeDialog = React.useCallback(() => onOpenChange(false), [onOpenChange]);

  const [isSaving, setIsSaving] = React.useState(false);
  const [localMapping, setLocalMapping] = React.useState<LocalMapping>(
    fieldsOnContactObject.reduce((a, attr) => {
      // a.mapFromBE[attr] = undefined;
      if (attr) a.editableMap[attr] = undefined;

      return a;
    }, getLocalMapping()),
  );

  React.useEffect(() => {
    (async () => {
      try {
        setLocalMapping((prev) => ({ ...prev, loading: true }));

        const { fields } = await getMapping();

        setLocalMapping((prev) => {
          const nextMapFromBE = Object.keys(fields).reduce<LocalMapping["mapFromBE"]>(
            (a, key) => {
              const v = fields[key];
              a[key] = v && { mappedTo: v.mappedTo, disabled: v.disabled };

              return a;
            },
            { ...prev.mapFromBE },
          );
          const nextEditableMap = Object.keys(prev.editableMap).reduce<LocalMapping["editableMap"]>(
            (a, key) => {
              const v = fields[key];
              a[key] = v && { mappedTo: v.mappedTo, disabled: v.disabled };

              return a;
            },
            { ...prev.editableMap },
          );

          return {
            loading: false,
            editableMap: nextEditableMap,
            mapFromBE: nextMapFromBE,
          };
        });
      } catch (e) {
        console.error("0C6pz0Ojh6 | Smth went wrong", e);
        notify(get(e, "message") || "Error. Please contact support.", { type: "error" });
      }
    })();
  }, [setLocalMapping, getMapping]);

  // /**
  //  * attributes that have no mappedTo value neither in BE map, nor\
  //  * in editable map
  //  */
  // const attrsThatHaveNoMappedTo = React.useMemo(
  //   () => {
  //     const merged = {
  //       ...localMapping.mapFromBE,
  //       ...localMapping.editableMap
  //     };
  //     return Object.entries(merged)
  //       .filter(([_, value]) => value && value.mappedTo === undefined && value.disabled !== true)
  //       .map(([attr]) => attr);
  //     // A valid target is one where the mapping exists (value is truthy) and the mappedTo value is not defined, and the mapping is not disabled
  //     // if value is undefined (falsy) then this means this mapping does not exist (either locally or on the server)
  //   },
  //   [localMapping]
  // );
  // The below code replaces the functionality of the above code,
  // Rather than defining valid mapping targets as other mappings that are mapped as is,
  // we look directly at all the mappedTo values of all mappings, as these will be the actual names of properties on created contacts.
  const specialOptionValues = specialOptions.map((o) => o.value);
  const mappedToTargets = React.useMemo(() => {
    const targets: Set<string> = new Set();
    for (const [attr, mapping] of Object.entries(localMapping.mapFromBE)) {
      if (mapping && mapping.disabled !== true) {
        const target = mapping.mappedTo ?? attr;
        if (!specialOptionValues.includes(target)) {
          targets.add(target);
        }
      }
    }
    return [...targets];
  }, [localMapping]);

  // We don't need this code anymore as the back-end will now fix double mappings through the transfer principle
  // We should allow the user to edit a mapping that is the target of another mapping as to prevent this would
  // make their life harder as they search for the other mapping that maps to the target they want to change.
  // /**
  //  * attributes that are not set as a value of mappedTo property \
  //  * for any attribute
  //  */
  // const attrsThatAreNotMappedTo = React.useMemo(
  //   () => {
  //     const merged = { ...localMapping.mapFromBE, ...localMapping.editableMap };

  //     const attrsThatAreMappedTo = Object.values(merged).reduce< Record< string, true > >(
  //       (a, value) => {
  //         if(value !== undefined && value.mappedTo !== undefined) {
  //           a[value.mappedTo] = true;
  //         }

  //         return a;
  //       },
  //       {}
  //     );

  //     return Object.keys(merged).reduce< Record< string, true > >(
  //       (a, attr) => {
  //         if( attrsThatAreMappedTo[attr] !== true ) {
  //           a[attr] = true;
  //         }

  //         return a;
  //       },
  //       {}
  //     );
  //   },
  //   [localMapping]
  // );
  const specialOptionLabels = specialOptions.map((o) => o.label);
  const options = React.useMemo<SelectOptions>(
    () => [
      {
        label: t("contacts_mapping_modal.special"),
        options: specialOptionLabels.map((label) => ({ value: label, label })),
      },
      {
        label: t("contacts_mapping_modal.other"),
        options: mappedToTargets.map((label) => ({ value: label, label })),
      },
    ],
    [specialOptionLabels, mappedToTargets, t],
  );

  const rows = React.useMemo(
    () =>
      Object.entries(localMapping.editableMap).map(([key, mapping]) => ({
        id: key,
        key,
        mapping,
      })),
    [localMapping.editableMap],
  );

  const columns = React.useMemo<DataTableColumn[]>(
    () => [
      {
        field: "key",
        headerName: t("contacts_mapping_modal.project_data"),
        flex: 1,
        minWidth: 220,
      },
      {
        field: "mappedTo",
        headerName: t("contacts_mapping_modal.central_person_data"),
        flex: 1,
        minWidth: 260,
        renderCell: (params) => {
          const key = params.row.key as string;
          const mapping = params.row.mapping as AttrMetadataMap[string];
          const value = mapping
            ? (specialOptions.find((o) => o.value === mapping.mappedTo)?.label ?? mapping.mappedTo)
            : "";

          if (localMapping.loading) {
            return <AutocompleteLoading className="h-10 !transform-none" />;
          }

          const filteredOptions: SelectOptions = options.map((group) => {
            if (!("options" in group)) return group;
            return {
              ...group,
              options: group.options.filter((opt) => opt.value !== key),
            };
          });

          return (
            <Select
              value={value || null}
              options={filteredOptions}
              allowArbitrary
              fullWidth={ true}
              menuPortalTarget={document.body}
              disabled={mapping?.disabled ?? false}
              onValueChange={(nextValue) => {
                if (nextValue === null) return;
                const special = specialOptions.find((it) => it.label === nextValue);
                const mappedToValue = special ? special.value : nextValue;
                setLocalMapping((prev) => ({
                  ...prev,
                  editableMap: {
                    ...prev.editableMap,
                    [key]: {
                      ...prev.editableMap[key],
                      mappedTo: mappedToValue,
                    },
                  },
                }));
              }}
            />
          );
        },
      },
      {
        field: "active",
        headerName: t("contacts_mapping_modal.active"),
        width: 120,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          const key = params.row.key as string;
          const mapping = params.row.mapping as AttrMetadataMap[string];
          const value = mapping
            ? (specialOptions.find((o) => o.value === mapping.mappedTo)?.label ?? mapping.mappedTo)
            : "";

          if (!mapping) {
            return null;
          }

          return (
            <Tooltip
              title={t("contacts_mapping_modal.activate_deactivate_mapping")}
              placement="right"
            >
              <div className="flex justify-start">
                <Checkbox
                  value={!(mapping?.disabled ?? false)}
                  onValueChange={(val) => {
                    setLocalMapping((prev) => ({
                      ...prev,
                      editableMap: {
                        ...prev.editableMap,
                        [key]: {
                          ...prev.editableMap[key],
                          disabled: !val,
                        },
                      },
                    }));
                  }}
                  disabled={value === undefined}
                />
              </div>
            </Tooltip>
          );
        },
      },
    ],
    [localMapping, options, specialOptionLabels, specialOptions, t],
  );

  const handleSave = React.useCallback(async () => {
    try {
      setIsSaving(true);
      await save(localMapping.editableMap);
      closeDialog();
    } catch (e) {
      console.error("Nt6Yz29jhQ | Smth went wrong", e);
      notify(get(e, "message") || "Error. Please contact support.", { type: "error" });
    } finally {
      setIsSaving(false);
    }
  }, [closeDialog, localMapping.editableMap, save]);

  const content = (
    <>
      <DialogHeader title={t("contacts_mapping_modal.title")} controls={{ allowResize: false }} />
      <Dialog.Body className="flex flex-col gap-4">
        <div
          className="w-full"
          style={{ maxHeight: "60vh" }}
        >
          <DataTable
            style={{ height: "100%" }}
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            hideFooter
          />
        </div>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.Actions.CancelButton
          label={t("cancel")}
          disabled={isSaving}
        >
          {t("cancel")}
        </Dialog.Actions.CancelButton>
        <Dialog.Actions.PrimaryButton
          label={t("save")}
          loading={isSaving}
          disabled={isSaving}
          onClick={handleSave}
        >
          {t("save")}
        </Dialog.Actions.PrimaryButton>
      </Dialog.Actions>
    </>
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      intent="form"
    >
      <Dialog.Content>
          {content}
      </Dialog.Content>
    </Dialog>
  );
};
